import { useState } from 'react'
import { Plus, Trash2, Check, X } from 'lucide-react'
import {
  useAttachPrice,
  useDetachPrice,
  useOrgSubscriptionItems,
  useStripePrices,
} from '../../api/admin-stripe'

interface OrgRow {
  org_id: string
  name: string
  status: string | null  // stripe_subscription_status
  has_subscription: boolean
}

/**
 * Per-org subscription-items editor.
 *
 * Picker → list of attached items → "Attach price…" inline form +
 * "Detach" with inline confirm (matching the PolicyGroup pattern, no
 * modal). Detach button disabled when there's only one item.
 *
 * The picker's options come from a parent-supplied org list — typically
 * the same list backing /admin?tab=storage and the org selector elsewhere.
 */
export function OrgSubscriptionItemsPanel({ orgs }: { orgs: OrgRow[] }) {
  const [selectedOrgId, setSelectedOrgId] = useState<string>(
    orgs.find((o) => o.has_subscription)?.org_id ?? orgs[0]?.org_id ?? '',
  )
  const items = useOrgSubscriptionItems(selectedOrgId || undefined)
  const prices = useStripePrices()
  const attach = useAttachPrice()
  const detach = useDetachPrice()
  const [adding, setAdding] = useState(false)
  const [pendingPriceId, setPendingPriceId] = useState<string>('')
  const [confirmDetach, setConfirmDetach] = useState<string | null>(null)

  const selectedOrg = orgs.find((o) => o.org_id === selectedOrgId)
  const subActive = items.data?.status === 'active' || items.data?.status === 'trialing'

  async function doAttach(e: React.FormEvent) {
    e.preventDefault()
    if (!selectedOrgId || !pendingPriceId) return
    try {
      await attach.mutateAsync({ orgId: selectedOrgId, priceId: pendingPriceId })
      setAdding(false)
      setPendingPriceId('')
    } catch {
      // error surfaces via attach.error
    }
  }

  async function doDetach(itemId: string) {
    if (!selectedOrgId) return
    try {
      await detach.mutateAsync({ orgId: selectedOrgId, itemId })
      setConfirmDetach(null)
    } catch {
      // error surfaces via detach.error
    }
  }

  return (
    <section className="overflow-hidden rounded-lg border border-border bg-surface">
      <header className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <div className="font-medium">Per-org subscription items</div>
        <select
          value={selectedOrgId}
          onChange={(e) => setSelectedOrgId(e.target.value)}
          className="rounded border border-border bg-background px-2 py-1 text-sm"
        >
          {orgs.map((o) => (
            <option key={o.org_id} value={o.org_id}>
              {o.name} {o.has_subscription ? '' : '(no sub)'}
            </option>
          ))}
        </select>
      </header>

      <div className="space-y-3 p-4">
        {selectedOrg && !selectedOrg.has_subscription ? (
          <div className="rounded border border-warning/30 bg-warning/5 p-3 text-sm text-text-secondary">
            This org has no Stripe subscription yet. The org owner needs to call{' '}
            <code>POST /api/billing/subscription</code> first.
          </div>
        ) : items.isLoading ? (
          <div className="text-sm text-text-secondary">Loading…</div>
        ) : items.isError ? (
          <div className="rounded border border-error/30 bg-error/5 p-3 text-xs text-error">
            {String(items.error)}
          </div>
        ) : !items.data ? null : (
          <>
            <div className="text-xs text-text-secondary">
              Subscription <code>{items.data.subscription_id}</code> · status{' '}
              <span className={subActive ? 'text-success' : 'text-warning'}>
                {items.data.status}
              </span>
            </div>

            <table className="w-full text-sm">
              <thead className="bg-surface-elevated text-xs uppercase text-text-secondary">
                <tr>
                  <th className="px-2 py-1 text-left">Item ID</th>
                  <th className="px-2 py-1 text-left">Price</th>
                  <th className="px-2 py-1 text-left">Type</th>
                  <th className="px-2 py-1 text-right">Amount</th>
                  <th className="px-2 py-1"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.data.items.map((it) => {
                  const isLast = items.data!.items.length <= 1
                  const isConfirming = confirmDetach === it.id
                  return (
                    <tr key={it.id}>
                      <td className="px-2 py-1 font-mono text-xs">{it.id}</td>
                      <td className="px-2 py-1 font-mono text-xs">{it.price.id}</td>
                      <td className="px-2 py-1 text-xs">
                        {it.price.metered
                          ? `metered ${it.price.recurring_interval ?? ''}`.trim()
                          : it.price.recurring_interval ?? '—'}
                      </td>
                      <td className="px-2 py-1 text-right font-mono text-xs">
                        {it.price.unit_amount != null
                          ? `$${(it.price.unit_amount / 100).toFixed(2)}`
                          : '—'}
                      </td>
                      <td className="px-2 py-1 text-right">
                        {isConfirming ? (
                          <div className="inline-flex gap-1">
                            <button
                              type="button"
                              onClick={() => doDetach(it.id)}
                              disabled={detach.isPending}
                              className="rounded bg-error p-1 text-white hover:opacity-90 disabled:opacity-50"
                              title="Confirm detach"
                            >
                              <Check className="h-3 w-3" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmDetach(null)}
                              className="rounded border border-border p-1 hover:bg-surface-elevated"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setConfirmDetach(it.id)}
                            disabled={isLast}
                            title={
                              isLast
                                ? 'Cannot detach the only item — cancel the subscription instead'
                                : 'Detach this price from the subscription'
                            }
                            className="inline-flex items-center gap-1 text-xs text-error hover:underline disabled:cursor-not-allowed disabled:text-text-secondary disabled:no-underline"
                          >
                            <Trash2 className="h-3 w-3" /> Detach
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            {!adding ? (
              <button
                type="button"
                onClick={() => setAdding(true)}
                disabled={!subActive}
                title={
                  subActive
                    ? 'Add a Price to this Subscription as a new SubscriptionItem'
                    : `Subscription is ${items.data.status} — only active/trialing subscriptions accept new items`
                }
                className="inline-flex items-center gap-1 rounded border border-border bg-background px-3 py-1.5 text-sm hover:bg-surface-elevated disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Plus className="h-3.5 w-3.5" /> Attach price
              </button>
            ) : (
              <form onSubmit={doAttach} className="flex items-center gap-2">
                <select
                  value={pendingPriceId}
                  onChange={(e) => setPendingPriceId(e.target.value)}
                  className="rounded border border-border bg-background px-2 py-1 text-sm"
                >
                  <option value="">Pick a Price…</option>
                  {(prices.data?.prices ?? []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.id} {p.nickname ? `· ${p.nickname}` : ''}
                    </option>
                  ))}
                </select>
                <button
                  type="submit"
                  disabled={!pendingPriceId || attach.isPending}
                  className="rounded bg-primary p-1.5 text-white hover:opacity-90 disabled:opacity-50"
                >
                  <Check className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => { setAdding(false); setPendingPriceId('') }}
                  className="rounded border border-border p-1.5 hover:bg-surface-elevated"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </form>
            )}

            {(attach.isError || detach.isError) && (
              <div className="rounded border border-error/30 bg-error/5 p-2 text-xs text-error">
                Stripe error: {String(attach.error ?? detach.error)}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  )
}
